import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiIconComponent } from '@/ui/icon/ui-icon';
import { ChangeDetectionStrategy,
    Component,
    type ElementRef,
    EventEmitter,
    Input,
    NgZone,
    type OnChanges,
    type OnDestroy,
    type OnInit,
    Output,
    type SimpleChanges,
    ViewChild,
    inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import type moment from 'moment';
import { sseMenuAndTitleColor } from '../../../../constants/color';
import { EllipsisWithTitleFallbackDirective } from '../../../../directives/ellipsis-with-title-fallback/ellipsis-with-title-fallback.directive';
import type { IpeTileData, TileRange } from '../../../../interfaces/Tile.interface';
import {
    type IpeTileCreationOutput,
    IpeTileCreationPopover,
    type IpeTileCreationPopoverParams
} from '../../../../pages/ipe-tile-creation/ipe-tile-creation';
import { LocalNumberPipe } from '../../../../pipes/localnumber.pipe';
import type { VatModes } from '../../../../providers/budgets/budgets.constants';
import { HighchartsStaticProvider } from '../../../../providers/highcharts/highcharts.static.provider';
import { ProjectsProvider } from '../../../../providers/projects/projects.provider';
import { TilesProvider } from '../../../../providers/tiles/tiles.provider';
import { AdvizeoModalService } from '../../../../services/advizeo-modal.service';
import { EditTextComponent } from '../../../form-controls/controls/edit-text/edit-text';
@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./tile-ipe-gauge-sub-component.scss'],
    selector: 'tile-ipe-gauge-sub-component',
    templateUrl: 'tile-ipe-gauge-sub-component.html',
    imports: [EditTextComponent, FormsModule, EllipsisWithTitleFallbackDirective, UiIconComponent, UiIconButtonComponent, TranslatePipe]
})
export class TileIpeGaugeSubComponentComponent implements OnInit, OnDestroy, OnChanges {
    private _translateService = inject(TranslateService);
    private _tilesProvider = inject(TilesProvider);
    private _advizeoModalService = inject(AdvizeoModalService);
    private _zone = inject(NgZone);
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _projectsProvider = inject(ProjectsProvider);

    private highChartsObject: Highcharts.Chart | null = null;
    private gaugeColorUndefined = '#6F6F6F';
    private gaugeColor = this.gaugeColorUndefined;
    private _vatMode!: VatModes;
    private _localNumberPipe: LocalNumberPipe;
    subtitle: string = '';
    @Input() tileData!: IpeTileData;
    @Input() forceBinding: boolean = true;

    @Input() dateStart!: moment.Moment;
    @Input() dateEnd!: moment.Moment;
    @Input() hasTileAdminOption!: boolean;

    @Output() deleted = new EventEmitter<void>();
    @Output() edited = new EventEmitter<IpeTileData>();

    @ViewChild('chartCanvas', { static: true }) canvas!: ElementRef;

    constructor() {
        this._localNumberPipe = new LocalNumberPipe();
    }

    ngOnInit() {
        this._projectsProvider
            .getVatMode$()
            .pipe(untilDestroyed(this))
            .subscribe(vatMode => {
                this._vatMode = vatMode!;
                this.buildChart();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.tileData || changes.forceBinding) {
            this.buildChart();
        }
    }

    updateRangeDescription() {
        if (this.tileData) {
            this.subtitle = this._tilesProvider.getRangeDescription(this.tileData);
        }
    }

    private buildChart() {
        if (!this.tileData || !this._vatMode) return;
        this._zone.runOutsideAngular(() => {
            if (!this.highChartsObject) {
                const options: Highcharts.Options = this.getChartConfig();
                options.chart!.events = this._highchartsStaticProvider.REFLOW_ON_CONTAINER_RESIZE.chart.events;
                this.highChartsObject = Highcharts.chart(this.canvas.nativeElement, options);
            }
            if (this.tileData) {
                const ranges: TileRange[] = this.tileData.description.ranges;
                if (ranges && ranges.length > 0) {
                    // Set yAxis plotBands
                    ranges.forEach((range: TileRange, index) => {
                        this.highChartsObject!.yAxis[0].addPlotBand({
                            id: String(index),
                            color: range.color,
                            from: range.start,
                            to: range.end,
                            outerRadius: '110%'
                        });
                    });

                    // Get min and max
                    const min = ranges.reduce((min, p) => (p.start < min ? p.start : min), ranges[0].start);
                    const max = ranges.reduce((max, p) => (p.end > max ? p.end : max), ranges[0].end);

                    // Set yAxis extremes
                    this.highChartsObject.yAxis[0].setExtremes(min, max);
                    this.highChartsObject.yAxis[0].options.tickInterval = max;

                    // Add value
                    const value: number =
                        this.tileData.value && Math.abs(this.tileData.value) < 10
                            ? +this.tileData.value.toFixed(2)
                            : Math.round(this.tileData.value);
                    const range = ranges.find(range => range.start <= value && value <= range.end);

                    if (range) {
                        this.gaugeColor = range.color;
                    } else {
                        this.gaugeColor = this.gaugeColorUndefined;
                    }

                    this.highChartsObject.series[0].setData([{ y: value, color: this.gaugeColor }]);
                    this.highChartsObject.update({
                        yAxis: {
                            tickPositions: [min, max]
                        }
                    });
                    this.highChartsObject.redraw(false);
                }
            }
        });
        this.updateRangeDescription();
    }

    private getChartConfig(): Highcharts.Options {
        const unitToDisplay = this._tilesProvider.getUnitWithTypeAndIndicatorToDisplay(this.tileData, true, this._vatMode);
        const errorConfig: boolean = this.tileData.error !== null;
        return this.generateChartConfig(unitToDisplay, errorConfig);
    }

    editTile(event: MouseEvent) {
        const params: IpeTileCreationPopoverParams = {
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            editMode: true,
            tile: this.tileData
        };
        event.stopPropagation();
        const popover = this._advizeoModalService.openModal(IpeTileCreationPopover, params);

        popover.afterClosed().then((data: IpeTileCreationOutput | undefined) => {
            if (data && data.edit) {
                if (data.tile && data.tile.tileType === this.tileData.tileType) {
                    this.tileData = data.tile;
                    this.highChartsObject = null;
                    this.buildChart();
                } else {
                    this.edited.next(data.tile);
                }
            }
        });
    }

    deleteTile(event: MouseEvent) {
        event.stopPropagation();
        this._advizeoModalService.openLegacyDialog({
            title: this._translateService.instant('IPE.DELETE_CONFIRMATION_TITLE'),
            message: this._translateService.instant('IPE.DELETE_CONFIRMATION_MESSAGE', { tileName: this.tileData.name }),
            buttons: [
                {
                    text: this._translateService.instant('BUTTONS.CANCEL'),
                    role: 'cancel'
                },
                {
                    text: this._translateService.instant('BUTTONS.DELETE'),
                    handler: () => {
                        this.processDeleteTile();
                    }
                }
            ]
        });
    }

    processDeleteTile() {
        this._tilesProvider.deleteTile(this.tileData.tileId, this.tileData.buildingId).then(() => {
            this.deleted.next();
        });
    }

    ngOnDestroy() {
        if (this.highChartsObject) {
            this.highChartsObject.destroy();
        }
    }

    updateTileName() {
        this._tilesProvider.renameTile(this.tileData.tileId, this.tileData.name);
    }

    generateChartConfig(unit: string, errorConfig: boolean): Highcharts.Options {
        const valueFormatter = (value: number): string => {
            return this._localNumberPipe.transform(value);
        };
        return this._highchartsStaticProvider.buildConfig(this._highchartsStaticProvider.DISABLE_ANIMATIONS, {
            chart: {
                type: 'solidgauge'
            },
            pane: {
                center: errorConfig ? ['50%', '70%'] : ['50%', '85%'],
                size: errorConfig ? '120%' : '150%',
                startAngle: -90,
                endAngle: 90,
                background: [
                    {
                        backgroundColor: 'transparent',
                        innerRadius: '60%',
                        outerRadius: '100%',
                        shape: 'arc'
                    }
                ]
            },

            tooltip: {
                enabled: false
            },

            xAxis: {},

            yAxis: {
                plotBands: [],
                lineWidth: 0,
                minorTicks: false,
                labels: {
                    y: 16
                }
            },
            series: [
                {
                    type: 'solidgauge',
                    data: [],
                    dataLabels: {
                        formatter(this: Highcharts.Point) {
                            return (
                                '<div style="text-align:center">' +
                                '<span style="font-size:25px;color:' +
                                sseMenuAndTitleColor +
                                '">' +
                                (errorConfig ? '--' : valueFormatter(this.y!)) +
                                '</span> <br/>' +
                                '<span style="font-size:12px;color:#6F6F6F">' +
                                unit +
                                '</span>' +
                                '</div>'
                            );
                        }
                    }
                }
            ],
            plotOptions: {
                solidgauge: {
                    dataLabels: {
                        y: -50,
                        borderWidth: 0,
                        useHTML: true
                    }
                }
            }
        });
    }
}
